import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { Button } from './ui/button';
import { Loader2, ShieldCheck, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function ConsentGate({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [checking, setChecking] = useState(true);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) { setChecking(false); return; }
    if (user.is_platform_admin) { setChecking(false); return; }

    const check = async () => {
      try {
        const res = await api.get('/legal/consent/check');
        setNeedsConsent(!res.data.consent_current);
      } catch {
        setNeedsConsent(false);
      }
      setChecking(false);
    };
    check();
  }, [isAuthenticated, user]);

  const accept = async () => {
    setAccepting(true);
    try {
      const versions = await api.get('/legal/versions');
      await api.post('/legal/consent', {
        terms_version: versions.data.terms_version,
        privacy_version: versions.data.privacy_version,
      });
      toast.success('Consent recorded');
      setNeedsConsent(false);
    } catch {
      toast.error('Failed to record consent');
    }
    setAccepting(false);
  };

  if (checking) return null;

  if (needsConsent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="consent-gate">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <Zap className="w-8 h-8 text-orange-500" />
            <span className="font-black text-2xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
          </div>

          <div className="bg-white rounded-sm border border-slate-200 shadow-lg p-8">
            <ShieldCheck className="w-8 h-8 text-orange-500 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Updated Terms & Privacy Policy</h2>
            <p className="text-sm text-slate-600 mb-6">
              Our Terms and Privacy Policy have been updated. Please review and accept them to continue using BidSentry.
            </p>
            <div className="flex flex-col gap-2 mb-6 text-sm">
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline underline-offset-2">
                Read Terms & Conditions
              </Link>
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline underline-offset-2">
                Read Privacy Policy
              </Link>
            </div>
            <Button
              onClick={accept}
              disabled={accepting}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="consent-accept-btn"
            >
              {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'I Accept — Continue'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
